/**
 * The files of a commit, a comparison or a pull request (AIOE 0.65.0), each drawn through ReviewPatch (copied from the
 * workbench): a header row per file, its lines folded where nothing changed. A commit's and a comparison's diff takes
 * no notes; a pull request's takes notes when the reader may comment, which the caller sends as one review comment.
 */

import { numberUnifiedDiff, type GitFileChange, type NoteInput, type ReviewNoteItem } from '@aioe/shared';
import { ChevronDown, ChevronRight } from '@gravity-ui/icons';
import { useMemo, useState } from 'react';

import { ReviewPatch } from './ReviewPatch.tsx';
import './reviewPatch.css';

const STATUS_WORDS: Record<GitFileChange['status'], string> = { added: 'Added', modified: 'Changed', removed: 'Deleted', renamed: 'Renamed' };
const LEFT_OUT: Record<NonNullable<GitFileChange['patchLeftOut']>, string> = {
  binary: 'A binary file: there are no lines to show.',
  'too-large': 'The changes to this file are too large to show here.',
  budget: 'Left out so the page stays quick to load.',
};

/** Notes on a pull request's lines: kept by the caller, sent as one review comment. */
export interface DiffNotes {
  notes: ReviewNoteItem[];
  onAdd(input: NoteInput): void;
  onEdit(id: string, text: string): void;
  onRemove(id: string): void;
  onReopen(id: string): void;
  onResolve(id: string): void;
  onSendBatch(): void;
}

const NOOP = () => undefined;
const READ_ONLY: DiffNotes = { notes: [], onAdd: NOOP, onEdit: NOOP, onRemove: NOOP, onReopen: NOOP, onResolve: NOOP, onSendBatch: NOOP };

/** "+12 −4", the sign as well as the colour. */
export function LineCounts({ additions, deletions }: { additions: number; deletions: number }) {
  return (
    <span className="whitespace-nowrap text-xs tabular-nums">
      <span className="text-success">+{additions.toLocaleString('en-AU')}</span> <span className="text-danger">−{deletions.toLocaleString('en-AU')}</span>
    </span>
  );
}

function FileDiff({ file, open, onToggle, notes, webUrl }: { file: GitFileChange; open: boolean; onToggle: () => void; notes: DiffNotes; webUrl?: string }) {
  const rows = useMemo(() => (file.patch ? numberUnifiedDiff(file.patch) : []), [file.patch]);
  const mine = useMemo(() => notes.notes.filter((n) => n.path === file.path), [notes.notes, file.path]);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <li className="grid gap-1.5">
      <button type="button" onClick={onToggle} aria-expanded={open} className="flex w-full min-w-0 cursor-(--cursor-interactive) items-center gap-2 rounded-lg px-1 py-1 text-start text-sm hover:bg-default/40">
        <Chevron className="size-3.5 shrink-0 text-muted" aria-hidden />
        <span className="min-w-0 flex-1 truncate font-mono text-sm" title={file.from ? `${file.from} → ${file.path}` : file.path}>
          {file.from ? <span className="text-muted">{file.from} → </span> : null}
          {file.path}
        </span>
        <span className="hidden shrink-0 text-xs text-muted sm:inline">{STATUS_WORDS[file.status]}</span>
        <LineCounts additions={file.additions} deletions={file.deletions} />
      </button>
      {open ? (
        rows.length ? (
          <div className="diff-box" data-readonly={notes === READ_ONLY || undefined}>
            <ReviewPatch rows={rows} defaultPath={file.path} fold notes={mine} onAdd={notes.onAdd} onEdit={notes.onEdit} onRemove={notes.onRemove} onReopen={notes.onReopen} onResolve={notes.onResolve} onSendBatch={notes.onSendBatch} />
          </div>
        ) : (
          <p className="ps-6 text-xs text-muted">
            {file.patchLeftOut ? LEFT_OUT[file.patchLeftOut] : file.status === 'renamed' ? 'Renamed with no changes to its lines.' : 'No lines changed.'}
            {file.patchLeftOut && file.patchLeftOut !== 'binary' && webUrl ? (
              <>
                {' '}
                <a href={webUrl} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                  See it on GitHub
                </a>
                .
              </>
            ) : null}
          </p>
        )
      ) : null}
    </li>
  );
}

/** Every file, the first few open; `notes` makes the lines take notes. */
export function FileDiffs({ files, notes, webUrl, openFirst = 3 }: { files: GitFileChange[]; notes?: DiffNotes; webUrl?: string; openFirst?: number }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(files.slice(0, openFirst).map((f) => f.path)));
  const toggle = (path: string) => setOpen((s) => (s.has(path) ? new Set([...s].filter((p) => p !== path)) : new Set([...s, path])));
  if (!files.length) return <p className="text-sm text-muted">No files changed.</p>;
  return (
    <ul className="grid gap-2">
      {files.map((f) => (
        <FileDiff key={f.path} file={f} open={open.has(f.path)} onToggle={() => toggle(f.path)} notes={notes ?? READ_ONLY} webUrl={webUrl} />
      ))}
    </ul>
  );
}
